/**
 * PillToggle — the two-shape button dialect for inline mode switches
 * (metric pickers, time windows). Active option is solid ink; inactive
 * options are hairline chips. Mono-uppercase labels throughout.
 */

interface PillToggleOption<T extends string> {
  value: T
  label: string
}

interface PillToggleProps<T extends string> {
  options: PillToggleOption<T>[]
  value: T
  onChange: (value: T) => void
  'aria-label': string
}

export function PillToggle<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
}: PillToggleProps<T>): React.JSX.Element {
  return (
    <div role="group" aria-label={ariaLabel} className="flex gap-1.5">
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => {
              onChange(option.value)
            }}
            className={`h-6 rounded-sm px-2.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
              active
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
